// External Imports
import { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { MdReceiptLong, MdRefresh } from "react-icons/md";
import { TbFileTypePdf } from "react-icons/tb";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import { isoDateToString } from "../../util/format-date";
import { toTitleCase } from "../../util/format-text";
import DetailPair from "../UI/DetailPair";
import LinkPair from "../UI/LinkPair";
import Heading from "../UI/Heading";
import Notes from "../UI/Notes";
import ImageSelector from "../UI/ImageSelector";
import DetailList from "../UI/DetailList";
import PersonCard from "../UI/PersonCard";
import InvoiceModal from "./InvoiceModal";
import ChargeInvoiceModal from "./ChargeInvoiceModal";
import MarkAsPaidModal from "./MarkAsPaidModal";
import RefundModal from "./RefundModal";
import PaymentRefunds from "./PaymentRefunds";
import SplitInvoiceModel from "./SplitInvoiceModal";
import PaymentDiscounts from "./PaymentDiscounts";
import PaymentDisputes from "./PaymentDisputes";
import AuditLogs from "../UI/AuditLogs";

/** ========================================================================
 * * Payment Information Page
 * Renders a page, which displays all information about a payment.
 * ========================================================================== */

export default function PaymentInfo() {
  // Load Context
  const { navigate, setConfirm } = useContext(UtilityContext);

  // Get ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [payment, setPayment] = useState({});
  const [loading, setLoading] = useState(true);
  const [chargeInvoice, setChargeInvoice] = useState(false);
  const [markAsPaid, setMarkAsPaid] = useState(false);
  const [chargeLateFee, setChargeLateFee] = useState(false);
  const [refund, setRefund] = useState(false);
  const [split, setSplit] = useState(false);

  // =================== FETCH PAYMENT DATA ===================
  const fetchPayment = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "payment",
        id: id,
        columns: [
          "customer { id defaultPaymentMethodId, paymentMethods: paymentMethods_on_customer { id brand last4 deleted } firstName lastName phone photo }",
          "booking { id startDate endDate vehicle { id year make { name } model cornerImage } }",
          "createdBy { id name photo type }",
          "paidTo { id name photo type }",
          "lastAttemptBy { id name photo type }",
          "paymentMethod { id brand last4 accountId }",
          "refunds: refunds_on_payment { dateCreated employee { name } amount reason stripeId }",
          "discounts: couponUsages_on_payment { couponId dateCreated }",
          "disputes: disputes_on_payment { id status dateCreated reason amount }",
        ],
      });
      setPayment(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== DELETE INVOICE ===================
  const deleteInvoiceHandler = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("delete-invoice", { paymentId: payment.id });
      navigate("/payments");
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== GENERATE RECEIPT ===================
  const receiptHandler = async () => {
    try {
      setLoading(true);
      const url = await requestAdminAPI("get-payment-receipt", {
        paymentId: payment.id,
      });
      window.open(url, "_blank");
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== CREATE STRIPE INVOICE ===================
  const stripeInvoiceHandler = async () => {
    try {
      setLoading(true);
      const link = await requestAdminAPI("create-stripe-invoice", {
        paymentId: payment.id,
      });
      fetchPayment();
      console.log(link);
      window.open(link, "_blank");
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  // =================== CHECK ACH STATUS ===================
  const checkAchStatusHandler = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("check-ach-status", {
        paymentId: payment.id,
      });
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== GET TOLLS PDF ===================
  const getTollsPDF = async () => {
    try {
      const vehicleId = payment.booking?.vehicle.id;
      if (!vehicleId) return;
      setLoading(true);
      const url = await requestAdminAPI("fetch-tolls", {
        vehicleId,
        sd: payment.booking?.startDate,
        ed: payment.booking?.endDate,
        pdf: true,
      });
      window.open(url, "_blank");
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== USE_EFFECT ===================
  useEffect(() => {
    fetchPayment();
  }, []);

  // =================== RENDER DEFAULT MENU
  const defaultMenu = () => {
    const paid = payment.status === "succeeded";
    const invoice = payment.type === "invoice";
    return (
      <>
        {!paid && invoice && (
          <button
            onClick={() =>
              payment.stripeLink
                ? setConfirm({
                    message:
                      "A stripe link has already been generated! Please confirm that this link has been exprired before generating another link.",
                    action: stripeInvoiceHandler,
                  })
                : stripeInvoiceHandler()
            }
            className="btn-secondary"
          >
            Generate Stripe Link
          </button>
        )}
        {payment.paymentMethod?.accountId && !paid && (
          <button onClick={checkAchStatusHandler} className="btn-secondary">
            Check Status
          </button>
        )}
        {!paid && invoice && new Date(payment.dueDate) < new Date() && (
          <button
            onClick={() => setChargeLateFee(true)}
            className="btn-secondary"
          >
            Charge Late Fee
          </button>
        )}
        {!paid && invoice && (
          <button onClick={() => setSplit(true)} className="btn-secondary">
            Split Invoice
          </button>
        )}
        {!paid && invoice && (
          <button onClick={() => setMarkAsPaid(true)} className="btn-primary">
            Mark as Paid
          </button>
        )}
        {!paid && invoice && (
          <button
            onClick={() => setChargeInvoice(true)}
            className="btn-primary"
          >
            Charge Invoice
          </button>
        )}
        {!paid && invoice && (
          <button
            onClick={() =>
              setConfirm({
                message: "Once deleted, the invoice cannot be recovered.",
                action: deleteInvoiceHandler,
              })
            }
            className="btn-warning"
          >
            Delete Invoice
          </button>
        )}
        {paid && (
          <button onClick={() => setRefund(true)} className="btn-primary">
            Issue Refund
          </button>
        )}
      </>
    );
  };

  // =================== RENDER MENU
  const renderMenu = () => {
    if (loading)
      return (
        <div className="grid gap-4">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="h-10 btn-secondary animate-pulse"></div>
          ))}
        </div>
      );
    else return defaultMenu();
  };

  if (!id || (!loading && (!payment || !payment.id)))
    return (
      <div className="w-full h-full p-8 rounded-lg bg-stone-900">
        <p className="text-stone-400">
          Payment ID Not Provided or Payment Not Found!
        </p>
      </div>
    );

  return (
    <div className="divider">
      <ChargeInvoiceModal
        open={chargeInvoice}
        setOpen={setChargeInvoice}
        invoice={payment}
        onComplete={fetchPayment}
      />
      <MarkAsPaidModal
        open={markAsPaid}
        setOpen={setMarkAsPaid}
        invoice={payment}
        onComplete={fetchPayment}
      />
      <RefundModal
        open={refund}
        setOpen={setRefund}
        payment={payment}
        onComplete={fetchPayment}
      />
      <SplitInvoiceModel
        open={split}
        setOpen={setSplit}
        payment={payment}
        onComplete={() => navigate(`/payments?query=${payment.id}`)}
      />
      <InvoiceModal
        open={chargeLateFee}
        setOpen={setChargeLateFee}
        customerId={payment.customer?.id}
        bookingId={payment.booking?.id}
        amount={(payment.amount * 0.05).toFixed(2)}
        description={`Late Fee for: "${payment.description}" (ID: ${payment.id})`}
        category="violation"
      />
      <div className="container gap-8 lg:col-span-3">
        <Heading
          title={`${
            payment.type === "invoice" ? "Invoice" : "Payment"
          } Details`}
          id={payment.id}
        >
          {payment?.category === "tolls" && (
            <MdReceiptLong className="icon" onClick={getTollsPDF} />
          )}
          <TbFileTypePdf className="icon" onClick={receiptHandler} />
          <AuditLogs table="payment" id={id} />
          <MdRefresh className="icon" onClick={fetchPayment} />
        </Heading>

        <div className="info">
          <DetailPair
            label="Date Created"
            value={isoDateToString(
              payment.dateCreated,
              "eee dd MMM, yyyy - hh:mm a"
            )}
            loading={loading}
          />
          <DetailPair
            label="Due Date"
            value={isoDateToString(payment.dueDate, "eee dd MMM, yyyy")}
            loading={loading}
          />
          <DetailPair
            label="Delinquency Date"
            value={isoDateToString(payment.delinquencyDate, "eee dd MMM, yyyy")}
            loading={loading}
          />
          <DetailPair
            label="Date Paid"
            value={isoDateToString(payment.datePaid, "eee dd MMM, yyyy")}
            loading={loading}
          />

          <DetailPair
            label="Paid via"
            value={payment.paidVia?.replace("#", "")}
            loading={loading}
          />

          <LinkPair
            label="Stripe ID"
            value={payment.stripeId}
            link={
              payment.stripeId
                ? `https://dashboard.stripe.com/payments/${payment.stripeId}`
                : false
            }
            loading={loading}
          />

          <LinkPair
            label="Stripe Invoice Link"
            value={payment.stripeLink ? "Link" : "-"}
            link={payment.stripeLink}
            loading={loading}
          />

          <DetailPair
            label="Status"
            classes={
              payment.status === "succeeded"
                ? "text-green-600 font-bold text-lg"
                : payment.status === "pending"
                ? new Date(payment.dueDate) < new Date()
                  ? "text-orange-500 font-bold text-lg"
                  : "text-yellow-500 font-bold text-lg"
                : payment.status === "delinquent"
                ? "text-red-600 font-bold text-lg"
                : "text-stone-200 font-bold text-lg"
            }
            value={
              payment.status === "pending" &&
              new Date(payment.dueDate) < new Date()
                ? "PAST DUE"
                : payment.status?.toUpperCase()
            }
            loading={loading}
          />

          <DetailPair
            label="Subtotal"
            value={`$${payment.subtotal?.toFixed(2)}`}
            loading={loading}
          />
          <DetailPair
            label="Discount"
            value={`-$${payment.discount?.toFixed(2)}`}
            loading={loading}
          />
          <DetailPair
            label="Taxes"
            value={`$${payment.taxes?.toFixed(2)}`}
            loading={loading}
          />
          <DetailPair
            label="Total Amount"
            classes={"font-bold text-lg"}
            value={`$${payment.amount?.toFixed(2)}`}
            loading={loading}
          />
          <DetailPair
            label="Refunded"
            value={`$${payment.refunded?.toFixed(2)}`}
            loading={loading}
          />
          <DetailPair
            label="Category"
            value={toTitleCase(payment.category)}
            loading={loading}
          />
          <div className="lg:col-span-4">
            <DetailPair
              label="Description"
              classes={"font-semibold text-stone-200 text-sm"}
              value={toTitleCase(payment.description)}
              loading={loading}
            />
          </div>
        </div>
        <PaymentRefunds stripeId={payment.stripeId} refunds={payment.refunds} />
        <PaymentDiscounts discounts={payment.discounts} />
        <PaymentDisputes disputes={payment.disputes} />
        <Notes table="payment" id={payment.id} />
      </div>
      <div className="space-y-4 sidebar">
        <div className="grid gap-8 overflow-y-scroll text-sm no-scrollbar">
          <PersonCard
            label="Customer:"
            name={`${payment.customer?.firstName} ${payment.customer?.lastName}`}
            secondary={payment.customer?.phone}
            image={payment.customer?.photo}
            link={`/customers/info?id=${payment.customer?.id}`}
            loading={loading}
          />
          {payment.booking && (
            <div
              onClick={(e) =>
                navigate(`/vehicles/info?id=${payment.booking?.vehicle?.id}`, e)
              }
              className="grid gap-4 clickable"
            >
              <ImageSelector
                image={payment.booking.vehicle?.cornerImage}
                classes="w-full min-h-[100px]"
                loading={loading}
              />
            </div>
          )}
          {payment.booking && (
            <DetailList
              label="Booking Details"
              values={[
                `${isoDateToString(
                  payment.booking?.startDate
                )} - ${isoDateToString(payment.booking?.endDate)}`,
                `${payment.booking?.vehicle?.year} ${payment.booking?.vehicle?.make?.name} ${payment.booking?.vehicle?.model}`,
                `${payment.booking?.vehicle?.id}`,
              ]}
              loading={loading}
              link={`/bookings/info?id=${payment.booking?.id}`}
            />
          )}
          {payment.createdBy && (
            <PersonCard
              label="Created By:"
              name={`${payment.createdBy?.name}`}
              secondary={payment.createdBy?.type}
              image={payment.createdBy?.photo}
              link={`/employees/info?id=${payment.createdBy?.id}`}
              loading={loading}
            />
          )}
          {payment.lastAttemptBy && (
            <PersonCard
              label="Last Attempt By:"
              name={`${payment.lastAttemptBy?.name}`}
              secondary={payment.lastAttemptBy?.type}
              image={payment.lastAttemptBy?.photo}
              link={`/employees/info?id=${payment.lastAttemptBy?.id}`}
              loading={loading}
            />
          )}
          {payment.paidTo && (
            <PersonCard
              label="Marked as Paid By:"
              name={`${payment.paidTo?.name}`}
              secondary={payment.paidTo?.type}
              image={payment.paidTo?.photo}
              link={`/employees/info?id=${payment.paidTo?.id}`}
              loading={loading}
            />
          )}
        </div>
        <div className="grid gap-2 text-sm">{renderMenu()}</div>
      </div>
    </div>
  );
}
